import type { OnboardingStatus } from "@xop-any-time/shared";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useCallback, useEffect, useState, type JSX } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../auth/AuthProvider.js";
import { ApiError } from "../../lib/api.js";
import { getOnboardingStatus } from "../../lib/onboardingApi.js";
import { AuthNotice, SubmitButton } from "../login/AuthCard.js";
import { CHANNEL_MANAGER_LINE, OnboardingLayout, WallCard } from "./OnboardingLayout.js";
import { RejectedState } from "./RejectedState.js";
import { SignDocumentsStep } from "./SignDocumentsStep.js";
import { UnderReviewStep } from "./UnderReviewStep.js";

/**
 * `/onboarding` — the wall (spec E §10.2).
 *
 * Four states, one read. `GET /api/onboarding/status` is the only source of
 * the status; the applicant cookie's copy is for routing and is never
 * rendered. The page POLLS that read every 45 s — but ONLY while the
 * documents are under review, because that is the only state a human on the
 * other side changes; polling on step 1 would be asking whether the
 * applicant has uploaded something they are looking at.
 *
 * PROMOTION (spec E §6.3). When the status comes back `active` WITH a
 * `user`, the BFF has already re-minted the full session cookie on that
 * response. `refresh()` re-reads `/api/auth/me`, `AuthProvider` swaps the
 * applicant for the user, and `navigate("/")` lands in the inner portal —
 * no re-login. `active` WITHOUT a user means gate 3 refused after activation
 * (a membership revoked in the same minute); the wall says so.
 */

/** 45 s: one CEO click and one screen change are one poll apart, and a
 * dozen applicants polling cost crm-api nothing it notices. */
export const POLL_MS = 45_000;

type State =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; status: OnboardingStatus };

export function OnboardingPage(): JSX.Element {
  const { applicant, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ kind: "loading" });

  const load = useCallback(async () => {
    try {
      const status = await getOnboardingStatus();
      setState({ kind: "ready", status });
    } catch (err) {
      // The BFF says the applicant cookie no longer names a live
      // application — expired, or the session ended. There is nothing to
      // retry: send them back to sign in rather than showing an error card
      // that will fail forever.
      if (err instanceof ApiError && (err.code === "applicant_session_required" || err.code === "session_required")) {
        await logout();
        navigate("/login", { replace: true });
        return;
      }
      setState({
        kind: "error",
        message:
          err instanceof ApiError
            ? err.message
            : "We couldn't check your application just now. Please try again in a moment.",
      });
    }
  }, [logout, navigate]);

  useEffect(() => {
    void load();
  }, [load]);

  // The poll, and only while under review.
  const current = state.kind === "ready" ? state.status.status : null;
  useEffect(() => {
    if (current !== "documents_submitted") return;
    const id = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(id);
  }, [current, load]);

  // Promotion: the BFF minted the full cookie; adopt it and go inside.
  const promoted = state.kind === "ready" && state.status.user !== undefined;
  useEffect(() => {
    if (!promoted) return;
    void (async () => {
      await refresh();
      navigate("/", { replace: true });
    })();
  }, [promoted, refresh, navigate]);

  const onSignOut = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const companyName =
    state.kind === "ready" && state.status.companyName ? state.status.companyName : applicant?.name ?? "";

  return (
    <OnboardingLayout companyName={companyName} status={current} onSignOut={() => void onSignOut()}>
      {state.kind === "loading" && (
        <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
          <CircularProgress size={28} sx={{ color: "accent.500" }} aria-label="Loading" />
        </Box>
      )}

      {state.kind === "error" && (
        <WallCard title="Your application">
          <Box sx={{ mt: 2.5, display: "grid", gap: 2 }}>
            <AuthNotice>{state.message}</AuthNotice>
            <Box component="form" onSubmit={(e) => { e.preventDefault(); setState({ kind: "loading" }); void load(); }}>
              <SubmitButton>Try again</SubmitButton>
            </Box>
          </Box>
        </WallCard>
      )}

      {state.kind === "ready" && state.status.status === "submitted" && (
        <SignDocumentsStep onSubmitted={(next) => setState({ kind: "ready", status: next })} />
      )}

      {state.kind === "ready" && state.status.status === "documents_submitted" && (
        <UnderReviewStep status={state.status} />
      )}

      {state.kind === "ready" && state.status.status === "active" && (
        <WallCard title="Your account is active">
          {state.status.user ? (
            <Typography component="p" sx={{ mt: 0.75, fontSize: 13.5, color: "text.secondary" }}>
              Opening your portal…
            </Typography>
          ) : (
            <>
              <Typography component="p" sx={{ mt: 0.75, fontSize: 13.5, lineHeight: 1.6, color: "text.secondary" }}>
                XOP Networks has activated your application, but your reseller access isn't ready yet. This
                usually resolves on its own within a few minutes — reload this page to check.
              </Typography>
              <Typography component="p" sx={{ mt: 3, pt: 2.5, borderTop: 1, borderColor: "divider", fontSize: 12, color: "text.secondary" }}>
                {CHANNEL_MANAGER_LINE}
              </Typography>
            </>
          )}
        </WallCard>
      )}

      {state.kind === "ready" && state.status.status === "rejected" && <RejectedState status={state.status} />}
    </OnboardingLayout>
  );
}
